import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { htmlToText } from "@/components/RichEditor";
import { Plus, Mail, Clock, Search, ChevronDown, Cog } from "lucide-react";

function LogRow({ l }) {
  const [html, setHtml] = useState(null);
  const [open, setOpen] = useState(false);
  const toggle = async () => {
    if (!open && html === null) {
      const { data } = await api.get(`/email/log/${l.id}`).catch(() => ({ data: {} }));
      setHtml(data.html || "");
    }
    setOpen((o) => !o);
  };
  return (
    <>
      <tr onClick={toggle} className="cursor-pointer hover:bg-slate-50" data-testid={`email-log-row-${l.id}`}>
        <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{new Date(l.created_at).toLocaleString("de-DE")}</td>
        <td className="px-4 py-3"><div className="font-medium text-slate-800">{l.customer_name}</div><div className="text-xs text-slate-400">{l.to}{l.redirected_to ? ` (Test → ${l.redirected_to})` : ""}</div></td>
        <td className="px-4 py-3 text-slate-600">{l.template_name}</td>
        <td className="px-4 py-3 text-slate-600">{l.subject}</td>
        <td className="px-4 py-3 text-slate-600">{l.sent_by}</td>
        <td className="px-2"><ChevronDown size={15} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} /></td>
      </tr>
      {open && (
        <tr><td colSpan={6} className="px-4 pb-4 bg-slate-50">
          {html ? <iframe title="Mail" sandbox="" srcDoc={html} className="w-full h-72 border border-slate-200 rounded-lg bg-white" data-testid={`email-log-content-${l.id}`} />
            : <p className="text-xs text-slate-400 py-2">Inhalt wurde bei dieser Mail nicht gespeichert.</p>}
        </td></tr>
      )}
    </>
  );
}

function Log({ log }) {
  const [q, setQ] = useState("");
  const [year, setYear] = useState("alle");
  const [kind, setKind] = useState("alle");
  const years = [...new Set(log.map((l) => (l.created_at || "").slice(0, 4)).filter(Boolean))].sort().reverse();
  const shown = log.filter((l) => (year === "alle" || (l.created_at || "").startsWith(`${year}-`))
    && (kind === "alle" || (kind === "kunden" ? !!l.customer_id : !!l.person_id))
    && (!q || `${l.customer_name} ${l.to} ${l.subject} ${l.template_name}`.toLowerCase().includes(q.toLowerCase())));
  return (
    <>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <h2 className="font-display text-lg font-semibold text-slate-800 flex items-center gap-2"><Clock size={18} /> Versand-Protokoll ({shown.length})</h2>
        <div className="flex gap-2 flex-wrap">
          <div className="relative"><Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Empfänger, Betreff…" className="pl-9 w-56 h-9" data-testid="email-log-search" /></div>
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="h-9 w-40" data-testid="email-log-kind"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="alle">Alle Empfänger</SelectItem><SelectItem value="kunden">Partner & Kunden</SelectItem><SelectItem value="mitwirkende">Mitwirkende</SelectItem></SelectContent>
          </Select>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="h-9 w-32" data-testid="email-log-year"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="alle">Alle Jahre</SelectItem>{years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto shadow-sm">
        <table className="w-full text-sm" data-testid="email-log-table">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr><th className="text-left px-4 py-3">Datum</th><th className="text-left px-4 py-3">Empfänger</th><th className="text-left px-4 py-3">Vorlage</th><th className="text-left px-4 py-3">Betreff</th><th className="text-left px-4 py-3">Gesendet von</th><th /></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {shown.map((l) => <LogRow key={l.id} l={l} />)}
            {shown.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Keine Mails gefunden.</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default function Emails() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [log, setLog] = useState([]);
  const [ms, setMs] = useState(null);

  useEffect(() => {
    api.get("/email-templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/email/log").then((r) => setLog(r.data)).catch(() => {});
    api.get("/ms365/status").then((r) => setMs(r.data)).catch(() => {});
  }, []);

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Mailvorlagen</h1>
          <p className="text-slate-500 mt-1">Vorlagen für den Versand und Systemmails – Kachel anklicken zum Ansehen und Bearbeiten.</p>
        </div>
        <Button onClick={() => navigate("/mails/vorlage/neu")} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="add-template-button">
          <Plus size={16} className="mr-1.5" /> Neue Vorlage
        </Button>
      </div>
      {ms && (
        <div className={`mb-6 rounded-xl border p-4 text-sm flex items-center justify-between gap-3 flex-wrap ${ms.configured ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`} data-testid="ms365-banner">
          <span>{ms.configured ? <>Versand aktiv über Microsoft 365 als <b>{ms.sender}</b>{ms.redirect_to && <> · <b data-testid="redirect-banner">Testmodus: alle Mails gehen nur an {ms.redirect_to}</b></>}</> : "Microsoft 365 ist noch nicht eingerichtet – E-Mails können noch nicht versendet werden."}</span>
          {!ms.configured && <Link to="/einstellungen/mail" className="font-semibold underline" data-testid="ms365-setup-link">Jetzt einrichten</Link>}
        </div>
      )}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mb-10">
        {[...templates].sort((a, b) => (b.system_key === "closing") - (a.system_key === "closing")).map((t) => (
          <button key={t.id} onClick={() => navigate(`/mails/vorlage/${t.id}`)} data-testid={`template-card-${t.id}`}
            className={`text-left border rounded-xl p-5 shadow-sm hover:shadow hover:border-slate-400 transition-[border-color,box-shadow] ${t.system ? "bg-slate-50 border-slate-300" : "bg-white border-slate-200"}`}>
            <div className="flex items-center gap-2 min-w-0">
              <div className={`h-9 w-9 rounded-lg grid place-items-center shrink-0 ${t.system ? "bg-[#0B132B] text-white" : "bg-blue-50 text-blue-600"}`}>{t.system ? <Cog size={17} /> : <Mail size={17} />}</div>
              <div className="font-semibold text-slate-900 truncate">{t.name}</div>
            </div>
            {t.system && <span className="inline-block mt-2 text-[11px] font-medium px-2 py-0.5 rounded bg-[#0B132B] text-white">Systemmail</span>}
            <div className="mt-3 text-sm font-medium text-slate-700">{t.subject}</div>
            <div className="mt-1 text-xs text-slate-500 line-clamp-3 whitespace-pre-line">{htmlToText(t.body)}</div>
          </button>
        ))}
        {templates.length === 0 && <div className="text-slate-400 text-sm">Noch keine Vorlagen.</div>}
      </div>
      <Log log={log} />
    </div>
  );
}
